import React, { useState, useEffect, useCallback } from 'react';
import BackToGameToast from './BackToGameToast';
import { useFavoritesStore, useIsFavorite } from '../../../favorites/store/favoritesStore';
import { useGameStartActions } from '../../hooks/useGameStart';
import { useAppStore } from 'store/app/appStore';
import { transactionTracker } from 'core/services/TransactionTracker';
import type IGameSettings from 'types/models/IGameSettings';

interface BackToGameToastWrapperProps {
    gameId: string;
    gameName: string;
    gameThumbnail: string;
    gameData: IGameSettings; // Full game object for favorites
}

const BackToGameToastWrapper: React.FC<BackToGameToastWrapperProps> = ({
    gameId,
    gameName,
    gameThumbnail,
    gameData
}) => {
    const { startGame } = useGameStartActions();
    const toggleFavorite = useFavoritesStore(state => state.toggleFavorite);
    const { viewMode, isSearchActive } = useAppStore(
        state => ({
            viewMode: state.viewMode,
            isSearchActive: state.isSearchActive
        })
    );

    const isFav = useIsFavorite(gameId);
    const [localIsFavorite, setLocalIsFavorite] = useState(isFav);

    useEffect(() => {
        setLocalIsFavorite(isFav);
    }, [isFav]);

    const handlePlay = useCallback((gameKey: string) => {
        // Track game open transaction for Sentry
        transactionTracker.trackGameOpen(gameName, gameKey, 'continue_playing', viewMode);

        // Start the game
        startGame(gameKey);
    }, [startGame, gameName, viewMode]);

    const handleFavorite = useCallback((gameKey: string) => {
        // Immediately update local state for instant feedback
        setLocalIsFavorite(!localIsFavorite);
        // Update global state with both gameKey and gameData (required for adding to favorites)
        toggleFavorite(gameKey, gameData);
    }, [toggleFavorite, localIsFavorite, gameData]);

    return (
        <BackToGameToast
            gameId={gameId}
            gameName={gameName}
            gameThumbnail={gameThumbnail}
            onPlay={handlePlay}
            onFavorite={handleFavorite}
            isFavorite={localIsFavorite}
            isSearchActive={isSearchActive}
        />
    );
};

export default BackToGameToastWrapper;
